import { useEffect, type KeyboardEvent } from 'react';
import { Select, Stack, TextField } from '@citibox-panel/ui';
import type { CarrierOption } from '@citibox-panel/api-client';
import { type LocationFilters } from '../../search-shell/model/shellState';
import { deliveryRangeOptions, parseDeliveryRange } from '../../../lib/deliveryRange';
import { useLocationCarriers } from '../hooks/useLocationCarriers';
import { useDeliveryStates } from '../hooks/useDeliveryStates';
import { canonicalStates, stateFilterOptions } from '../lib/stateFilterOptions';

export interface FiltersStepProps {
  filters: LocationFilters;
  onChange: (filters: LocationFilters) => void;
  // Runs the search. The step owns no action of its own — the two buttons live in the dialog's
  // footer — so this is the ONE gesture inside the form that confirms it, and the caller decides
  // what confirming means.
  onSubmit: () => void;
  locationId: number;
  fetchLocationCarriers: (locationId: number) => Promise<CarrierOption[]>;
  fetchDeliveryStates: () => Promise<string[]>;
}

// Not `''`, which is what a placeholder-backed select uses: the façade hands MUI `displayEmpty` only
// alongside a `placeholder`, and this field has none — so a `''`-valued option would leave the closed
// control blank instead of naming the default. Cannot collide with a real option: those are integers.
const ALL_CARRIERS = 'all';

// The two halves travel together or not at all, so dropping them has ONE home. A spread cannot
// delete, and `exactOptionalPropertyTypes` rejects an explicit `undefined`.
function withoutCarrier({
  carrier: _carrier,
  carrierLabel: _label,
  ...rest
}: LocationFilters): LocationFilters {
  return rest;
}

// Step 2's BODY — the three filters and nothing else. The building it filters over is drawn by
// `FiltersHeader` in the dialog's title slot and the two actions sit in its footer slot, so what is
// left here is the form: it holds no draft, and the only thing it reads is the building's carriers.
export function FiltersStep({
  filters,
  onChange,
  onSubmit,
  locationId,
  fetchLocationCarriers,
  fetchDeliveryStates,
}: FiltersStepProps) {
  const { options, isPending, isError } = useLocationCarriers(locationId, fetchLocationCarriers);
  const {
    options: stateCatalog,
    isPending: statesPending,
    isError: statesError,
  } = useDeliveryStates(fetchDeliveryStates);

  const hasNoHistory = !isPending && !isError && options.length === 0;

  // Carried as a description and never as `error`: an optional filter with no options is degraded,
  // not invalid, and `error` would write `aria-invalid` on a control working exactly as designed.
  // The failure line names the CONSEQUENCE, because the reset below alters a value the agent set and
  // a description on the control is the only signal of it assistive tech reaches.
  // TODO: i18n
  const carrierHint = isPending
    ? 'Cargando los carriers de este edificio…'
    : isError
      ? 'No se pudieron cargar los carriers de este edificio; se ha quitado el filtro.'
      : hasNoHistory
        ? 'Este edificio no tiene entregas de ningún carrier.'
        : undefined;

  // Same three degraded readings as the carrier's, same reason for each: a description and never an
  // `error`, and a failure line that names the CONSEQUENCE because the fallback drops a filter the
  // agent set. The catalog is global, so its empty case says nothing about this building.
  // TODO: i18n
  const stateHint = statesPending
    ? 'Cargando los estados…'
    : statesError
      ? 'No se pudieron cargar los estados; se ha quitado el filtro.'
      : stateCatalog.length === 0
        ? 'No hay estados disponibles.'
        : undefined;

  // The catalog PLUS whatever is applied and not in it yet. Costs nothing — the label of a state
  // resolves from the app's own vocabulary with no read behind it — and it is what keeps the closed
  // control naming what the agent applied while the catalog is still in flight, instead of falling
  // back to a wire spelling. Once the catalog settles, the intersection below removes any stand-in
  // that turned out not to exist.
  const applied = filters.states ?? [];
  const stateOptions = stateFilterOptions([
    ...stateCatalog,
    ...applied.filter((state) => !stateCatalog.includes(state)),
  ]);

  const isApplied = filters.carrier !== undefined;
  const isListed = options.some((option) => option.id === filters.carrier);

  // `localeCompare('es')` on a copy: a raw `<` orders «Zeta» before «Álava» on code points, and a
  // MySQL collation would not match JS anyway — the panel sorts, so the read's order is free.
  const listed = [...options]
    .sort((a, b) => a.name.localeCompare(b.name, 'es'))
    .map((option) => ({ value: String(option.id), label: option.name }));

  const carrierOptions = [
    // The default leads and is deliberately NOT sorted in: it is the absence of a filter, not an
    // entry of the list. TODO: i18n
    { value: ALL_CARRIERS, label: 'Todos los carriers' },
    ...listed,
    // A controlled select renders blank for a value none of its options carries, and two windows
    // produce exactly that: while the read is in flight, and on the frame it settles without the
    // applied carrier. One condition covers both — gating this on `isPending` would blank the
    // control for the frame between settling and the reset below, which is what clears it. Guarded
    // on BOTH halves of the pair rather than asserted with `!`, so the invariant is visible here.
    ...(isApplied && !isListed && filters.carrierLabel !== undefined
      ? [{ value: String(filters.carrier), label: filters.carrierLabel }]
      : []),
  ];

  // One path for the three cases the reset must treat alike — absent from a successful list, a
  // failed read, an empty one — because in all three the SETTLED list does not hold the carrier.
  // An effect and not render-phase seeding: the draft is the dialog's state and this reacts to data.
  // It cannot loop, since the next render has no carrier and returns early.
  useEffect(() => {
    if (isPending || filters.carrier === undefined) return;
    if (options.some((option) => option.id === filters.carrier)) return;
    onChange(withoutCarrier(filters));
  }, [isPending, options, filters, onChange]);

  // Narrowed back from the string the DOM speaks to the integer the read is keyed by, with the name
  // reported beside it — the chips have to name the carrier the moment a tab is restored, which an
  // id alone cannot do. Both come off the same list ENTRY, so the pair always describes one carrier.
  const handleCarrier = (value: string) => {
    if (value === ALL_CARRIERS) {
      onChange(withoutCarrier(filters));
      return;
    }
    const picked = options.find((option) => String(option.id) === value);
    if (picked === undefined) return;
    onChange({ ...filters, carrier: picked.id, carrierLabel: picked.name });
  };

  // The control only ever offers the five valid values, so `null` is unreachable — but `tsc` does
  // not know that, and swallowing it is the same call the URL schema makes for an out-of-range day.
  const handleRange = (value: string) => {
    const days = parseDeliveryRange(value);
    if (days === null) return;
    onChange({ ...filters, days });
  };

  // `states := states ∩ catalog`, and an EMPTY intersection is the absent filter. One expression
  // covers all four readings: a catalog missing one applied state takes only that one, and a failed
  // or empty catalog takes the filter whole — so the fail-open comes for free instead of being a
  // separate branch that can disagree with this one. Gated on the settled read, or a hot «Editar»
  // would clear itself mid-flight; it cannot loop, because the next render has nothing left to drop.
  useEffect(() => {
    if (statesPending || filters.states === undefined) return;
    const kept = filters.states.filter((state) => stateCatalog.includes(state));
    if (kept.length === filters.states.length) return;
    const { states: _dropped, ...rest } = filters;
    onChange({ ...rest, ...(kept.length > 0 ? { states: kept } : {}) });
  }, [statesPending, stateCatalog, filters, onChange]);

  // Emitted in click order and stored in canonical order — the array reaches a cache key and a
  // request, and two orderings of one selection would be two of both. An emptied selection is
  // «every state», which is the ABSENT key and never `[]`: a spread cannot delete, so the drop is
  // written out.
  const handleStates = (values: string[]) => {
    const { states: _dropped, ...rest } = filters;
    onChange({ ...rest, ...(values.length > 0 ? { states: canonicalStates(values) } : {}) });
  };

  // An emptied box is not a filter, and settling that here is what spares every reader downstream
  // an emptiness check.
  const handleBox = (value: string) => {
    const trimmed = value.trim();
    const { box: _dropped, ...rest } = filters;
    onChange({ ...rest, ...(trimmed ? { box: trimmed } : {}) });
  };

  // Enter runs the search from the one control that can spare it: the box is the step's only
  // free-text field, and the three selects above it already answer Enter with MUI's own open, move
  // and pick — claiming it there would cost every select in the product its keyboard contract.
  // No other key is claimed, so Escape keeps bubbling to the dialog, which is what owns closing.
  //
  // `preventDefault` is what makes the keystroke land ONCE. `onSubmit` closes the dialog inside
  // this very keydown, and closing hands focus back to whatever opened it — «Editar» on the
  // deliveries screen, a real <button>. The rest of the same physical keypress would then activate
  // THAT: the dialog reopens on the way out and reads as one that will not close. Prevented here
  // and not in the caller because this is where the key is consumed, and there is nothing else for
  // Enter to do in a field that is in no form.
  const handleBoxKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    onSubmit();
  };

  return (
    // Stood off its header by one more step than the fields are from each other: the dialog's title
    // slot is not ruled off on this step (the frames draw no line), so the whitespace is the only
    // thing separating the building from the filters applied to it.
    <Stack spacing={2} sx={{ pt: 2 }}>
      {/* TODO: i18n */}
      <Select
        label="Fecha de entrega"
        value={String(filters.days)}
        onChange={handleRange}
        options={deliveryRangeOptions}
        // The façade forwards no ref, so autoFocus is the only route into the visible control.
        autoFocus
        fullWidth
      />
      {/* No `placeholder` and no `required`: the first renders an unpickable option, which is the
          opposite of a default, and the second announced a requirement this filter no longer has.
          TODO: i18n */}
      <Select
        label="Carrier"
        value={isApplied ? String(filters.carrier) : ALL_CARRIERS}
        onChange={handleCarrier}
        options={carrierOptions}
        // Only this field is affected, and only while in flight: every other control and the
        // caller's «Buscar» stay live, so a read that never lands still leaves a usable search.
        loading={isPending}
        {...(carrierHint !== undefined ? { helperText: carrierHint } : {})}
        fullWidth
      />
      {/* Third of four, between the carrier and the box: the two filters a courier case reaches for
          stand together. No `required` and no `placeholder` — «Todos» names the default in the
          closed control without putting an unpickable row in the list. TODO: i18n */}
      <Select
        label="Estado"
        multiple
        value={filters.states ?? []}
        onChange={handleStates}
        options={stateOptions}
        emptyLabel="Todos"
        // Only this field, and only while in flight: the other three controls and the caller's
        // «Buscar» stay live, so a catalog that never lands still leaves a usable search.
        loading={statesPending}
        {...(stateHint !== undefined ? { helperText: stateHint } : {})}
        fullWidth
      />
      {/* TODO: i18n */}
      <TextField
        label="Número de buzón"
        value={filters.box ?? ''}
        onChange={handleBox}
        onKeyDown={handleBoxKeyDown}
        fullWidth
      />
    </Stack>
  );
}
